import React, { useState } from "react";
import { Rnd } from "react-rnd";
import CloseIcon from "../../../assets/images/custom_documentation/close-input.svg";
import SettingsIcon from "../../../assets/images/custom_documentation/settings-input.svg";
import DuplicateIcon from "../../../assets/images/custom_documentation/duplicate-input.svg";
import { getFieldPlaceholder } from "../utils/fieldTypes";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MultiSelectDropdown from "../MultiSelectDropdown";
import "./style.scss";
import ReqIcon from "../../../assets/images/custom_documentation/req_icon.svg";

const formatLocalDate = (date) => {
  if (!date) return "";
  return `${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}-${date.getFullYear()}`;
};

const FieldRenderer = ({
  fields,
  pageNumber,
  scale,
  modalSettingsObj,
  getFieldConfig,
  getFieldSettings,
  handleMouseDown,
  handleDuplicateField,
  handleSettingsClick,
  handleDeleteField,
  handleFieldChange,
  handleProvideESignature,
  placedFields,
  setPlacedFields,
  isViewMode = false,
  isTaskViewMode = false,
  validationErrors = {},
}) => {
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [focusedFieldId, setFocusedFieldId] = useState(null);
  const [liveFieldLayout, setLiveFieldLayout] = useState({});

  const updateLiveFieldLayout = (fieldId, layout) => {
    setLiveFieldLayout((prev) => ({ ...prev, [fieldId]: layout }));
  };

  const clearLiveFieldLayout = (fieldId) => {
    setLiveFieldLayout((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const updatePlacedField = (fieldId, updates) => {
    setPlacedFields((prevFields) =>
      prevFields.map((f) =>
        f.id === fieldId && f.pageNumber === pageNumber
          ? { ...f, ...updates }
          : f,
      ),
    );
  };

  const handleDropdownOpen = (fieldId) => {
    setActiveFieldId(fieldId);
  };

  const handleDropdownClose = (fieldId) => {
    if (activeFieldId === fieldId) {
      setActiveFieldId(null);
    }
  };

  const handleSignatureClick = (field, e) => {
    if (isTaskViewMode) {
      e.stopPropagation();
      handleProvideESignature(field);
    }
  };

  const handleDuplicateClick = (field, e) => {
    e.stopPropagation();
    e.preventDefault();
    handleDuplicateField(field);
  };

  const handleSettingsButtonClick = (field, e) => {
    e.stopPropagation();
    e.preventDefault();
    handleSettingsClick(field);
  };

  const handleDeleteClick = (fieldId, e) => {
    e.stopPropagation();
    e.preventDefault();
    handleDeleteField(fieldId);
  };

  const handleControlMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const parseSignatureData = (value) => {
    if (!value) return null;

    if (typeof value === "string" && value.startsWith("{")) {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }

    return value;
  };

  const renderSignatureImage = (src) => {
    if (!src?.startsWith("data:image/")) return null;

    return (
      <div className="signature-display">
        <img
          src={src}
          alt="Signature"
          style={{
            maxWidth: "100%",
            maxHeight: "60px",
            objectFit: "contain",
          }}
        />
      </div>
    );
  };

  const renderSignatureContent = (
    signatureData,
    fontStyles,
    signerType = "",
    inTaskViewMode = false,
  ) => {
    // In task view mode, show signature image if userSigned flag is true (user signed in task view)
    // regardless of signerType, or if signerType is "Mine"
    // In normal mode (admin view), only show signature image when signerType is "Mine"
    // Never show signature image when signerType is "Employee" in normal mode
    if (inTaskViewMode) {
      const userSigned = signatureData?.userSigned === true;
      if (userSigned || signerType === "Mine") {
        if (
          typeof signatureData === "string" &&
          signatureData.startsWith("data:image/")
        ) {
          return renderSignatureImage(signatureData);
        }

        if (signatureData?.signature?.startsWith("data:image/")) {
          return renderSignatureImage(signatureData.signature);
        }
      }
    } else {
      // Normal mode (CustomClientDocument page)
      if (signerType === "Employee") {
        return (
          <span style={{ ...fontStyles, fontStyle: "normal" }}>(Signature)</span>
        );
      }

      if (signerType === "Mine") {
        if (
          typeof signatureData === "string" &&
          signatureData.startsWith("data:image/")
        ) {
          return renderSignatureImage(signatureData);
        }

        if (signatureData?.signature?.startsWith("data:image/")) {
          return renderSignatureImage(signatureData.signature);
        }
      }
    }

    return (
      <span style={{ ...fontStyles, fontStyle: "normal" }}>(Signature)</span>
    );
  };

  const getSignatureContainerClass = (signerType) => {
    switch (signerType) {
      case "Mine":
        return "mine-signature-container";
      case "Employee":
      case "":
      case null:
      case undefined:
        return "employee-signature-container";
      default:
        return "default-signature-container";
    }
  };

  const normalizeFieldType = (fieldType) => {
    switch (fieldType) {
      case "text":
      case "Text":
      case "Text input":
      case "text-input":
        return "text";
      case "textarea":
      case "Text mulit line input":
      case "text-mulitline":
        return "textarea";
      case "date":
      case "Date":
        return "date";
      case "select":
      case "Dropdown":
      case "dropdown":
        return "select";
      case "checkbox":
      case "Checkbox":
        return "checkbox";
      case "radio":
      case "Radio":
        return "radio";
      case "initials":
      case "Initials":
      case "initial":
        return "initials";
      case "signature":
      case "Signature":
        return "signature";
      default:
        return fieldType;
    }
  };

  const renderFieldContent = (field, fontStyles) => {
    const commonProps = {
      placeholder: field.placeholder || getFieldPlaceholder(field.type),
      value: field.value || "", // Ensure empty string when value is undefined
      onChange: (e) => handleFieldChange(field.id, e.target.value),
      style: fontStyles,
    };

    const normalizedType = normalizeFieldType(field.type);

    switch (normalizedType) {
      case "text":
        return (
          <div className="field-input-container field-container">
            <input
              type="text"
              {...commonProps}
              className="field-input"
              id={`field-${field.id}`}
              name={`field-${field.id}`}
              maxLength={
                field.characterLimit ||
                getFieldSettings(field, modalSettingsObj).characterLimit
              }
            />
          </div>
        );

      case "textarea":
        return (
          <div className="field-textarea-container field-container">
            <textarea
              {...commonProps}
              className="field-textarea"
              id={`field-${field.id}`}
              name={`field-${field.id}`}
              maxLength={
                field.characterLimit ||
                getFieldSettings(field, modalSettingsObj).characterLimit
              }
            />
          </div>
        );

      case "date":
        return (
          <div className="field-date-container field-container">
            <DatePicker
              selected={field.value ? new Date(field.value) : null}
              onChange={(date) =>
                handleFieldChange(field.id, formatLocalDate(date))
              }
              placeholderText={
                field.placeholder || getFieldPlaceholder(field.type)
              }
              className="field-date"
              style={fontStyles}
              id={`field-${field.id}`}
              name={`field-${field.id}`}
              onCalendarOpen={() => handleDropdownOpen(field.id)}
              onCalendarClose={() => handleDropdownClose(field.id)}
              dateFormat="MM-dd-yyyy"
              dropdownMode="select"
              showMonthDropdown
              showYearDropdown
              maxDate={new Date()}
              openToDate={field.value ? new Date(field.value) : null}
            />
            <span
              className="calendar-icon"
              onClick={() => {
                const dateInput = document.getElementById(`field-${field.id}`);
                if (dateInput) {
                  dateInput.focus();
                  dateInput.click();
                }
              }}
            ></span>
          </div>
        );

      case "select":
        return (
          <div className="field-select-container field-container">
            {field.multiple_options ? (
              <MultiSelectDropdown
                options={field.options || []}
                value={field.value || []}
                onChange={(newValue) => handleFieldChange(field.id, newValue)}
                placeholder="Select an option"
                style={fontStyles}
                id={`field-${field.id}`}
                name={`field-${field.id}`}
                onOpen={() => handleDropdownOpen(field.id)}
                onClose={() => handleDropdownClose(field.id)}
              />
            ) : (
              <select
                className="field-select"
                style={fontStyles}
                value={field.value || ""}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                id={`field-${field.id}`}
                name={`field-${field.id}`}
              >
                <option value="">Select an option</option>
                {field.options?.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div className="field-checkbox-container field-container">
            <input
              type="checkbox"
              className="field-checkbox"
              checked={field.value === "true" || field.value === true}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              id={`field-${field.id}`}
              name={`field-${field.id}`}
            />
          </div>
        );

      case "radio":
        // Fallback: if no radio_group is defined, group radio buttons by page
        const radioGroupName = field.radio_group
          ? `radio-group-${field.radio_group}`
          : `radio-page-${field.pageNumber || "default"}`;

        return (
          <div className="field-radio-container field-container">
            <input
              type="radio"
              className="field-radio"
              id={`field-${field.id}`}
              name={radioGroupName}
              checked={field.value === "true" || field.value === true}
              onChange={(e) => {
                if (e.target.checked) {
                  handleFieldChange(field.id, true);
                }
              }}
            />
          </div>
        );

      case "initials":
        return (
          <div className="field-initials-container field-container">
            <input
              type="text"
              placeholder={field.placeholder || getFieldPlaceholder(field.type)}
              value={field.value || ""}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only letters and full stops, remove all other special characters
                const filteredValue = value.replace(/[^a-zA-Z.]/g, "");
                handleFieldChange(field.id, filteredValue);
              }}
              className="field-initials"
              id={`field-${field.id}`}
              name={`field-${field.id}`}
              maxLength={5}
              style={{ ...fontStyles, textTransform: "uppercase" }}
            />
          </div>
        );

      case "signature":
        return (
          <div
            className={`field-signature field-container${
              isTaskViewMode ? " task-view-mode" : ""
            }`}
            onClick={(e) => handleSignatureClick(field, e)}
          >
            {(() => {
              const signatureData = parseSignatureData(field.value);

              if (!signatureData) {
                return (
                  <div className="default-signature-container">
                    {renderSignatureContent(
                      field.value,
                      fontStyles,
                      "",
                      isTaskViewMode,
                    )}
                  </div>
                );
              }

              const signerType = signatureData?.signerType || "";
              const containerClass = getSignatureContainerClass(signerType);

              return (
                <div className={containerClass}>
                  {renderSignatureContent(
                    signatureData,
                    fontStyles,
                    signerType,
                    isTaskViewMode,
                  )}
                </div>
              );
            })()}
          </div>
        );

      default:
        return null;
    }
  };

  const getFieldDimensions = (field) => {
    const fieldConfig = getFieldConfig(field);
    const dimensions = fieldConfig?.dimensions || "275x40";
    const baseWidth =
      field.width ||
      (dimensions.includes("px")
        ? parseInt(dimensions)
        : parseInt(dimensions.split("x")[0]));
    const baseHeight =
      field.height ||
      (dimensions.includes("x") ? parseInt(dimensions.split("x")[1]) : 40);

    return {
      width: baseWidth * scale,
      height: baseHeight * scale,
    };
  };

  const getFieldFontStyles = (field) => {
    const fieldSettings = getFieldSettings(field, modalSettingsObj);
    return {
      fontSize: field.fontSize
        ? `${field.fontSize}px`
        : fieldSettings.fontSize
          ? `${fieldSettings.fontSize}px`
          : undefined,
      fontFamily: field.fontStyle || fieldSettings.fontStyle || undefined,
    };
  };

  const isFieldElevated = (fieldId) =>
    activeFieldId === fieldId || focusedFieldId === fieldId;

  const getFieldRndClassName = (fieldId, fieldIndex) => {
    const stackIndex = Math.min(fieldIndex, 199);
    const stateClass = isFieldElevated(fieldId)
      ? "field-active"
      : "field-inactive";
    return `field-rnd-container field-stack-${stackIndex} ${stateClass}`;
  };

  const getFieldLayoutPosition = (field, width) => {
    const live = liveFieldLayout[field.id];
    return {
      x: live?.x ?? field.x * scale,
      y: live?.y ?? field.y * scale,
      width: live?.width ?? width,
    };
  };

  const renderFieldControls = (field, width) => {
    if (isViewMode || isTaskViewMode) return null;

    const controlsWidth = 62;
    const controlsRightOffset = 8;
    const controlsTopOffset = 12;
    const { x, y, width: fieldWidth } = getFieldLayoutPosition(field, width);

    return (
      <div
        key={`controls-${field.id}`}
        className="field-controls field-controls--overlay"
        style={{
          top: y - controlsTopOffset,
          left: x + fieldWidth - controlsRightOffset - controlsWidth,
        }}
        onMouseEnter={() => setFocusedFieldId(field.id)}
        onMouseLeave={() =>
          setFocusedFieldId((prev) => (prev === field.id ? null : prev))
        }
      >
        <button
          className="control-btn duplicate-btn"
          onClick={(e) => handleDuplicateClick(field, e)}
          onMouseDown={handleControlMouseDown}
          title="Duplicate"
        >
          <img src={DuplicateIcon} alt="Duplicate" />
        </button>
        {getFieldConfig(field)?.settings && (
          <button
            className="control-btn settings-btn"
            onClick={(e) => handleSettingsButtonClick(field, e)}
            onMouseDown={handleControlMouseDown}
            title="Settings"
          >
            <img src={SettingsIcon} alt="Settings" />
          </button>
        )}
        <button
          className="control-btn close-btn"
          onClick={(e) => handleDeleteClick(field.id, e)}
          onMouseDown={handleControlMouseDown}
          title="Delete"
        >
          <img src={CloseIcon} alt="Delete" />
        </button>
      </div>
    );
  };

  const pageFields = fields.filter((field) => field.pageNumber === pageNumber);

  return (
    <>
      {pageFields.map((field, fieldIndex) => {
        const { width, height } = getFieldDimensions(field);
        const fontStyles = getFieldFontStyles(field);

        return (
          <React.Fragment key={field.id}>
            {isTaskViewMode ? (
              <Rnd
                size={{
                  width: width,
                  height: height,
                }}
                position={{
                  x: field.x * scale,
                  y: field.y * scale,
                }}
                onDragStop={(e, d) =>
                  updatePlacedField(field.id, {
                    x: d.x / scale,
                    y: d.y / scale,
                  })
                }
                onResizeStop={(e, direction, ref, delta, position) =>
                  updatePlacedField(field.id, {
                    width: ref.offsetWidth / scale,
                    height: ref.offsetHeight / scale,
                    x: position.x / scale,
                    y: position.y / scale,
                  })
                }
                minWidth={50}
                minHeight={30}
                bounds="parent"
                className={getFieldRndClassName(field.id, fieldIndex)}
                onMouseEnter={() => setFocusedFieldId(field.id)}
                onMouseLeave={() =>
                  setFocusedFieldId((prev) => (prev === field.id ? null : prev))
                }
                enableResizing={false}
                disableDragging={true}
              >
                <div className="field-group">
                  <div className="placed-field" data-field-id={field.id}>
                    <div className="field-wrapper">
                      {renderFieldContent(field, fontStyles)}
                    </div>
                  </div>
                </div>
                {validationErrors[field.id] && field.needed && (
                  <div className="field-validation-error">
                    <p className="mb-0 err-feedback">
                      <img src={ReqIcon} alt="required-icon" />
                      This field is mandatory
                    </p>
                  </div>
                )}
              </Rnd>
            ) : (
              <Rnd
                size={{
                  width: width,
                  height: height,
                }}
                position={{
                  x: field.x * scale,
                  y: field.y * scale,
                }}
                onDrag={(e, d) => {
                  updateLiveFieldLayout(field.id, {
                    x: d.x,
                    y: d.y,
                    width,
                  });
                }}
                onDragStop={(e, d) => {
                  clearLiveFieldLayout(field.id);
                  updatePlacedField(field.id, {
                    x: d.x / scale,
                    y: d.y / scale,
                  });
                }}
                onResize={(e, direction, ref, delta, position) => {
                  updateLiveFieldLayout(field.id, {
                    x: position.x,
                    y: position.y,
                    width: ref.offsetWidth,
                  });
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  clearLiveFieldLayout(field.id);
                  updatePlacedField(field.id, {
                    width: ref.offsetWidth / scale,
                    height: ref.offsetHeight / scale,
                    x: position.x / scale,
                    y: position.y / scale,
                  });
                }}
                minWidth={50}
                minHeight={30}
                bounds="parent"
                className={getFieldRndClassName(field.id, fieldIndex)}
                onMouseEnter={() => setFocusedFieldId(field.id)}
                onMouseLeave={() =>
                  setFocusedFieldId((prev) => (prev === field.id ? null : prev))
                }
                enableResizing={
                  !isViewMode
                    ? {
                        top: false,
                        right: false,
                        bottom: false,
                        left: false,
                        topRight: true,
                        bottomRight: true,
                        bottomLeft: true,
                        topLeft: true,
                      }
                    : false
                }
                disableDragging={isViewMode}
              >
                <div className="field-group">
                  <div className="placed-field" data-field-id={field.id}>
                    <div className="field-wrapper">
                      {renderFieldContent(field, fontStyles)}
                    </div>
                  </div>
                </div>
              </Rnd>
            )}
          </React.Fragment>
        );
      })}
      {!isViewMode && !isTaskViewMode && (
        <div className="field-controls-overlay">
          {[...pageFields]
            .sort((a, b) => {
              if (a.id === focusedFieldId) return 1;
              if (b.id === focusedFieldId) return -1;
              return 0;
            })
            .map((field) => {
              const { width } = getFieldDimensions(field);
              return renderFieldControls(field, width);
            })}
        </div>
      )}
    </>
  );
};

export default FieldRenderer;
